/**
 * Decodes a stretch of a video's sound to mono PCM at a chosen sample rate.
 *
 * Everything goes through the browser's own decoder (decodeAudioData), fed a
 * small, self-contained stream built from the samples read out of the
 * container: AAC frames get ADTS headers, MP3 frames are already a stream,
 * and PCM is wrapped as a WAV. Decoding in batches of about a minute keeps
 * memory flat on long recordings.
 *
 * Timing: a decoder may drop priming samples from the start of what it is
 * given, but never from the end, so each batch's output is placed by its end
 * (the end of its last frame). Every sample then sits at its presentation
 * time, which is what lining angles up needs.
 */

import type { ByteSource } from './byteSource';
import {
  AacCodec,
  AudioTrack,
  FrameTable,
  MediaReadError,
  PacketTable,
  PcmCodec,
  coalesceRuns,
  frameAt,
  frameTime,
  packetRange,
  packetTime,
  planFrameReads,
  readPackets,
  readRuns,
  sliceFromRuns,
} from './isoAudio';
import { mp3DecoderLeadSamples } from './mp3Calibration';

export interface PcmClip {
  /** Mono samples. */
  samples: Float32Array;
  sampleRate: number;
  /** Presentation seconds of samples[0]. */
  startS: number;
}

/** Turns an encoded stream (ADTS, MP3, WAV) into mono samples at `sampleRate`. */
export type StreamDecoder = (bytes: Uint8Array, sampleRate: number) => Promise<Float32Array>;

const BATCH_SECONDS = 60;
/** Bytes of PCM decoded at once. */
const PCM_BATCH_BYTES = 24 * 1024 * 1024;
/**
 * PCM sits in short chunks between runs of video; reading the video between
 * nearby chunks costs less than a request per chunk.
 */
const PCM_MAX_GAP_BYTES = 256 * 1024;

// ---------------------------------------------------------------------------
// Stream builders
// ---------------------------------------------------------------------------

/** The 7-byte ADTS header (no CRC) for one raw AAC frame. */
export const adtsHeader = (codec: AacCodec, payloadLength: number): Uint8Array => {
  const length = payloadLength + 7;
  if (length > 0x1fff) throw new MediaReadError('corrupt', 'An AAC frame is too large.');
  const profile = codec.objectType - 1;
  const header = new Uint8Array(7);
  header[0] = 0xff;
  header[1] = 0xf1; // MPEG-4, layer 0, no CRC
  header[2] = (profile << 6) | (codec.samplingIndex << 2) | ((codec.channelConfig >> 2) & 1);
  header[3] = ((codec.channelConfig & 3) << 6) | ((length >> 11) & 3);
  header[4] = (length >> 3) & 0xff;
  header[5] = ((length & 7) << 5) | 0x1f; // buffer fullness 0x7ff: variable bit rate
  header[6] = 0xfc;
  return header;
};

/** Raw AAC frames as an ADTS stream. */
export const toAdts = (codec: AacCodec, frames: Uint8Array[]): Uint8Array => {
  let total = 0;
  for (let i = 0; i < frames.length; i += 1) total += frames[i].length + 7;
  const out = new Uint8Array(total);
  let at = 0;
  for (let i = 0; i < frames.length; i += 1) {
    out.set(adtsHeader(codec, frames[i].length), at);
    out.set(frames[i], at + 7);
    at += frames[i].length + 7;
  }
  return out;
};

const concat = (parts: Uint8Array[]): Uint8Array => {
  let total = 0;
  for (let i = 0; i < parts.length; i += 1) total += parts[i].length;
  const out = new Uint8Array(total);
  let at = 0;
  for (let i = 0; i < parts.length; i += 1) {
    out.set(parts[i], at);
    at += parts[i].length;
  }
  return out;
};

const writeAscii = (view: DataView, at: number, text: string) => {
  for (let i = 0; i < text.length; i += 1) view.setUint8(at + i, text.charCodeAt(i));
};

/**
 * Interleaved PCM frames as a little-endian WAV. Big-endian input (QuickTime
 * twos/in24/in32/fl32) is byte-swapped; 8-bit audio becomes unsigned, as WAV
 * requires.
 */
export const pcmToWav = (codec: PcmCodec, data: Uint8Array): Uint8Array => {
  const bytesPerSample = codec.bits / 8;
  const frames = Math.floor(data.length / codec.bytesPerFrame);
  const dataLength = frames * codec.bytesPerFrame;
  const out = new Uint8Array(44 + dataLength);
  const view = new DataView(out.buffer);
  writeAscii(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataLength, true);
  writeAscii(view, 8, 'WAVE');
  writeAscii(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, codec.float ? 3 : 1, true);
  view.setUint16(22, codec.channels, true);
  view.setUint32(24, Math.round(codec.sampleRate), true);
  view.setUint32(28, Math.round(codec.sampleRate) * codec.bytesPerFrame, true);
  view.setUint16(32, codec.bytesPerFrame, true);
  view.setUint16(34, codec.bits, true);
  writeAscii(view, 36, 'data');
  view.setUint32(40, dataLength, true);

  if (codec.littleEndian || bytesPerSample === 1) {
    out.set(data.subarray(0, dataLength), 44);
  } else {
    for (let at = 0; at < dataLength; at += bytesPerSample) {
      for (let b = 0; b < bytesPerSample; b += 1) out[44 + at + b] = data[at + bytesPerSample - 1 - b];
    }
  }
  if (bytesPerSample === 1 && !codec.unsigned) {
    // WAV 8-bit is unsigned; QuickTime twos 8-bit is signed.
    for (let at = 44; at < out.length; at += 1) out[at] = (out[at] + 128) & 0xff;
  }
  return out;
};

// ---------------------------------------------------------------------------
// The browser decoder
// ---------------------------------------------------------------------------

type OfflineContextCtor = new (channels: number, length: number, sampleRate: number) => OfflineAudioContext;

const offlineContextCtor = (): OfflineContextCtor | null => {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { OfflineAudioContext?: OfflineContextCtor; webkitOfflineAudioContext?: OfflineContextCtor };
  return w.OfflineAudioContext ?? w.webkitOfflineAudioContext ?? null;
};

/** Averages a buffer's channels into one. */
const downmix = (buffer: AudioBuffer): Float32Array => {
  const channels = buffer.numberOfChannels;
  if (channels === 1) return buffer.getChannelData(0).slice();
  const out = new Float32Array(buffer.length);
  for (let c = 0; c < channels; c += 1) {
    const data = buffer.getChannelData(c);
    for (let i = 0; i < out.length; i += 1) out[i] += data[i];
  }
  for (let i = 0; i < out.length; i += 1) out[i] /= channels;
  return out;
};

/**
 * Shrinks a signal by an integer factor, averaging each group: the low-pass
 * is crude, but it is only used when the browser can't decode at a low rate.
 */
const decimate = (input: Float32Array, factor: number): Float32Array => {
  if (factor <= 1) return input;
  const out = new Float32Array(Math.floor(input.length / factor));
  for (let i = 0; i < out.length; i += 1) {
    let sum = 0;
    for (let k = 0; k < factor; k += 1) sum += input[i * factor + k];
    out[i] = sum / factor;
  }
  return out;
};

/** Rates to try when a browser refuses a low-rate OfflineAudioContext. */
const FALLBACK_RATES = [16000, 24000, 48000];

/** The browser's decodeAudioData, decoding (and resampling) to mono at `sampleRate`. */
export const webAudioDecoder: StreamDecoder = async (bytes, sampleRate) => {
  const Ctor = offlineContextCtor();
  if (!Ctor) throw new MediaReadError('unsupported-audio', 'This browser cannot decode audio.');
  let context: OfflineAudioContext | null = null;
  let contextRate = sampleRate;
  const candidates = [sampleRate].concat(FALLBACK_RATES.filter((rate) => rate > sampleRate && rate % sampleRate === 0));
  for (let i = 0; i < candidates.length && !context; i += 1) {
    try {
      context = new Ctor(1, 1, candidates[i]);
      contextRate = candidates[i];
    } catch {
      context = null;
    }
  }
  if (!context) throw new MediaReadError('unsupported-audio', 'This browser cannot decode audio at this rate.');
  const copy = bytes.slice().buffer;
  const decoded = await new Promise<AudioBuffer>((resolve, reject) => {
    // The callback form works in every browser; older Safari has no promise form.
    const result = (context as OfflineAudioContext).decodeAudioData(copy, resolve, (error) =>
      reject(error ?? new MediaReadError('unsupported-audio', 'The audio could not be decoded.'))
    );
    if (result && typeof (result as Promise<AudioBuffer>).catch === 'function') {
      (result as Promise<AudioBuffer>).catch(() => undefined);
    }
  });
  return decimate(downmix(decoded), Math.round(contextRate / sampleRate));
};

// ---------------------------------------------------------------------------
// Reading a stretch of sound
// ---------------------------------------------------------------------------

export interface ReadAudioOptions {
  signal?: AbortSignal;
  /** 0..1 */
  onProgress?: (fraction: number) => void;
  decoder?: StreamDecoder;
  concurrency?: number;
}

/** Copies decoded samples that start at `outStartS` into `target` (which starts at `targetStartS`). */
export const place = (
  target: Float32Array,
  targetStartS: number,
  source: Float32Array,
  sourceStartS: number,
  sampleRate: number,
  fromS: number,
  toS: number
) => {
  const first = Math.max(0, Math.round((Math.max(fromS, sourceStartS) - targetStartS) * sampleRate));
  const last = Math.min(target.length, Math.round((Math.min(toS, sourceStartS + source.length / sampleRate) - targetStartS) * sampleRate));
  const shift = Math.round((targetStartS - sourceStartS) * sampleRate);
  for (let i = first; i < last; i += 1) {
    const j = i + shift;
    if (j >= 0 && j < source.length) target[i] = source[j];
  }
};

const throwIfAborted = (signal?: AbortSignal) => {
  if (signal?.aborted) {
    const error = new Error('The operation was aborted.');
    error.name = 'AbortError';
    throw error;
  }
};

/**
 * Mono samples for presentation seconds [startS, endS) of the track, at
 * `sampleRate`. Stretches without audio (before the track starts, after it
 * ends) are silence.
 */
export const readAudioClip = async (
  source: ByteSource,
  track: AudioTrack,
  startS: number,
  endS: number,
  sampleRate: number,
  options: ReadAudioOptions = {}
): Promise<PcmClip> => {
  const decoder = options.decoder ?? webAudioDecoder;
  const from = Math.max(0, startS);
  const to = Math.max(from, Math.min(endS, track.durationSeconds));
  const samples = new Float32Array(Math.max(0, Math.round((to - from) * sampleRate)));
  const clip: PcmClip = { samples, sampleRate, startS: from };
  if (samples.length === 0) return clip;

  // PCM batches are kept to a few tens of megabytes: a minute of a 96 kHz
  // eight-channel recorder file would otherwise be decoded in one go.
  const batchSeconds = track.codec.kind === 'pcm'
    ? Math.max(5, Math.min(BATCH_SECONDS, Math.floor(PCM_BATCH_BYTES / (track.codec.bytesPerFrame * track.codec.sampleRate))))
    : BATCH_SECONDS;
  const batches: Array<[number, number]> = [];
  for (let at = from; at < to; at += batchSeconds) batches.push([at, Math.min(to, at + batchSeconds)]);

  for (let b = 0; b < batches.length; b += 1) {
    throwIfAborted(options.signal);
    const [batchStart, batchEnd] = batches[b];
    const report = (fraction: number) => options.onProgress?.((b + Math.min(1, fraction)) / batches.length);

    if (track.codec.kind === 'pcm') {
      const codec = track.codec;
      const firstFrame = Math.max(0, frameAt(track, batchStart));
      const endFrame = Math.max(firstFrame, frameAt(track, batchEnd) + 1);
      const pieces = planFrameReads(track.table as FrameTable, codec.bytesPerFrame, firstFrame, endFrame);
      if (pieces.length === 0) continue;
      const runs = coalesceRuns(pieces, PCM_MAX_GAP_BYTES);
      const data = await readRuns(source, runs, {
        concurrency: options.concurrency,
        signal: options.signal,
        onProgress: (done, total) => report((done / total) * 0.8),
      });
      // Chunks hold consecutive frames, so the pieces join into one stream;
      // a gap in frame numbers (a damaged index) starts a new group.
      let group: Uint8Array[] = [];
      let groupStart = pieces[0].firstFrame;
      let nextFrame = groupStart;
      const flush = async () => {
        if (group.length === 0) return;
        const decoded = await decoder(pcmToWav(codec, concat(group)), sampleRate);
        place(samples, from, decoded, frameTime(track, groupStart), sampleRate, batchStart, batchEnd);
        group = [];
      };
      for (let p = 0; p < pieces.length; p += 1) {
        const piece = pieces[p];
        if (piece.firstFrame !== nextFrame) {
          await flush();
          groupStart = piece.firstFrame;
        }
        group.push(sliceFromRuns(runs, data, piece.offset, piece.length));
        nextFrame = piece.firstFrame + piece.frames;
      }
      await flush();
      report(1);
      continue;
    }

    const table = track.table as PacketTable;
    // An MP3 frame's data can start a few frames back (the bit reservoir).
    const [first, end] = packetRange(track, batchStart, batchEnd, track.codec.kind === 'aac' ? 2 : 3);
    if (end <= first) continue;
    const packets = await readPackets(source, track, first, end, {
      concurrency: options.concurrency,
      signal: options.signal,
      onProgress: (done, total) => report((done / total) * 0.8),
    });
    throwIfAborted(options.signal);
    const stream = track.codec.kind === 'aac' ? toAdts(track.codec, packets) : concat(packets);
    const decoded = await decoder(stream, sampleRate);
    const last = end - 1;
    const batchEndTime = packetTime(track, last) + table.durations[last] / track.timescale;
    // Safari's MP3 decoder outputs its sound a decoder delay early (see mp3Calibration).
    const mp3Lead = track.codec.kind === 'mp3' && decoder === webAudioDecoder ? await mp3DecoderLeadSamples(decoder) : 0;
    const decodedStart = batchEndTime - decoded.length / sampleRate + mp3Lead / track.codec.sampleRate;
    place(samples, from, decoded, decodedStart, sampleRate, batchStart, batchEnd);
    report(1);
  }
  return clip;
};
